import React, { useEffect, useState } from 'react';
import AppStorage from 'easy-app-storage';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
    MultiProvider,
    ApiProvider,
    storageOptions,
    I18nProvider,
    StorageProvider,
    api,
    SpringBigThemeProvider,
    ThemeProvider,
    SpringBigStoreProvider,
    AppSettingsProvider,
    StashboardProvider,
    ThemeOverrides,
    ClientType,
    DutchieStoreProvider,
} from '@springbig/shared';
import AppRoutes from './routes';
import SplashScreen from './shared/SplashScreen/SplashScreen';
import lodashMerge from 'lodash/merge';
import DefaultLang from '@springbig/shared/src/i18n/defaults';
import DefaultLangES from '@springbig/shared/src/i18n/defaults_es';
import DefaultLangFR from '@springbig/shared/src/i18n/defaults_fr';

const appStorage = new AppStorage(localStorage, storageOptions);
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 0
        }
    }
});

const App = () => {

    const [settings, setSettings] = useState();
    const [languageEdit, setLanguageEdit] = useState();
    const [language, setLanguage] = useState();
    const [client, setClient] = useState<ClientType>();
    const [theme, setTheme] = useState<ThemeOverrides>();
    const [isConfigsLoading, setIsConfigLoading] = useState<boolean>(false);

    useEffect(() => {

        const fetchData = async () => {
            try {
                const response = await fetch(process.env.PUBLIC_URL + '/static/data.json');
                const data = await response.json();
                setClient(data.clientName);
            }
            catch (error) {
                console.error('Error fetching JSON data:', error);
            }
        };

        fetchData();

    }, [process.env.PUBLIC_URL]);

    const loadJsonData = async () => {

        try {

            if (client) {

                setIsConfigLoading(true);

                const _settings = await api.get(`/native_app/v1/app_settings?name=${client}`);

                if (_settings) {

                    setIsConfigLoading(false);

                    setTheme({
                        ..._settings,
                        toast_text: '#FFFFFF',
                        toast_success_bg: '#28a644',
                        toast_warning_bg: '#ffc107',
                        toast_info_bg: '#17a2b8',
                        toast_error_bg: '#dc3545',
                    });
                    setSettings({
                        bottomNav: _settings.navigation,
                        merchantOrg: _settings.merchantOrg,
                        ..._settings.settings,
                        skeleton_start_color: _settings?.skeleton_start_color ?? '#c6c6c6',
                        skeleton_end_color: _settings?.skeleton_end_color ?? '#f3f3f3',
                        appTitle: _settings.appTitle
                    });

                    setLanguageEdit(_settings.englishOverride as any);
                    setLanguage(_settings.englishOverride);

                    if (_settings.settings.languages && _settings.settings.languages.length > 0) {

                        _settings.settings.languages.map(lang => {

                            if (lang.default) {
                                let selectedLanguage = DefaultLang;
                                switch (lang.label) {
                                case 'English (US)':
                                    break;
                                case 'French (FR)':
                                    selectedLanguage = lodashMerge({}, selectedLanguage, DefaultLangFR);
                                    break;
                                case 'Spanish (ES)':
                                    selectedLanguage = lodashMerge({}, selectedLanguage, DefaultLangES);
                                    break;
                                }

                                setLanguage(lodashMerge({}, selectedLanguage, lang));
                            }
                        });
                    }
                }
            }
        }
        catch (error) {
            const message = `%cApp setting for ${client} could not be loaded from database
Please check that your merchant's merchant_organization matches your app name
Also check that app:qaMerchant in your localStorage doesn't override your app name
People from the Native App Team are here to help...`;

            const style = 'font-size: 14px; font-weight: bold; color: white; background-color: red; padding: 4px; border-radius: 4px;';

            console.log(message, style);
            console.error(message, style);

            throw new Error(`App setting for ${client} could not be loaded from database.`);
        }
    };

    useEffect(() => {

        loadJsonData();
    }, [client]);

    const updateMerchant = (clientType: ClientType) => {

        setClient(clientType);
    };

    if (isConfigsLoading) {
        // adding the legacy splashscreen in while waiting for the new animated one
        return <SplashScreen />;
    }

    return (
        <ThemeProvider theme={theme || {}} key={'theme'}>
            <I18nProvider language={language || {}} languagesEdit={languageEdit || {}} key={5}>
                <MultiProvider
                    providers={[
                        <QueryClientProvider client={queryClient} key={0} />,
                        <StorageProvider storage={appStorage} key={1}  />,
                        <SpringBigStoreProvider queryClient={queryClient} key={2}  />,
                        <ApiProvider api={api} key={3}  />,
                        <SpringBigThemeProvider themeConfig={theme} key={4}  />,
                        <AppSettingsProvider {...settings} key={6}  />,
                        <StashboardProvider key={7}  />,
                        <DutchieStoreProvider key={8}  />
                    ]}
                >
                    <AppRoutes updateMerchant={updateMerchant} />
                </MultiProvider>
            </I18nProvider>
        </ThemeProvider>
    );
};

export default App;
